import { createSlice } from '@reduxjs/toolkit';
import { prepareBlockAsync, getPriceEsimByUserParametersAsync, getSubscribersAsync } from './EsimUpgradeBlockAsyncThunk';
import { getEsimTypeCode } from '../../../utilities/domain/simTypeConstants';
import { getUrlFromEpiLink } from '../../../utilities/StringHelpers/StringHelpers';

const initialState = {
    page: '',
    esimBlockData: {},
    loginDetails: {
        identity: '',
        subscriberNo: '',
        otpTicketId: '',
        securityOtpTicketId: '',
        securityOtpPhone: '',
        restrictedCodeRequired: false,
    },
    esimPrice: '',
    email: '',
    subscriberList: [],
    qrCodeDetails: {
        base64QrCode: '',
        activationCode: '',
        smdp: '',
        accessCode: ''
    },
    isGetQrCodeError: false,
    isLoadingQrCode: false,
    checkUserResponse: {
        isJet: false,
        primaryEmail: '',
        idNumber: '',
        isBusiness: false
    },
    orderDetails: {
        orderId: ''
    },
    isWhatsAppActive: false,
    isCellcomAppActive: false,
    tinyAppUrl: '',
    errorDescription: '',
};

const esimUpgradeBlockSlice = createSlice({
    name: 'esimUpgradeBlock',
    initialState: initialState,
    reducers: {
        resetBlock: (state) => initialState,
        initBlock: {
            reducer: (state, action) => {
                state.esimBlockData = action.payload.esimBlockData;
            },
            prepare: (action) => {
                const currentBlock = action.esimUpgradeBlock;
                const esimContentData = currentBlock.esimContentData.expandedValue[0];

                let esimBlockData = {
                    blockId: esimContentData.contentLink.id,
                    upgCourse: esimContentData.upgCourse.value,
                    productId: esimContentData.productId.value,
                    package: esimContentData.package.value,
                    combSeq: esimContentData.combSeq.value,
                    isAdvanceOrder: !!esimContentData.isAdvanceOrder.value,
                    headerRemark: esimContentData.headerRemark.value,
                    cellularDevicesSupportedUrl: esimContentData.cellularDevicesSupportedUrl.value,

                    otpTopIcon: esimContentData.otpTopIcon.value?.url,
                    otpRecievedCodeIcon: esimContentData.otpRecievedCodeIcon.value?.url,
                    otpPhoneTitle: esimContentData.otpPhoneTitle.value,
                    otpPhoneSubTitle: esimContentData.otpPhoneSubTitle.value,
                    otpCodeTitle: esimContentData.otpCodeTitle.value,
                    otpCodeSubTitle: esimContentData.otpCodeSubTitle.value,

                    whatIsEsimPopupText: esimContentData.whatIsEsimPopupText.value,
                    isDeviceSupportEsimPopupText: esimContentData.isDeviceSupportEsimPopupText.value,
                    deleteEsimPopupText: esimContentData.deleteEsimPopupText.value,
                    disconnectLinePopupText: esimContentData.disconnectLinePopupText.value,

                    isDisplayWhatIsEsimPopup: !!esimContentData.isDisplayWhatIsEsimPopup.value,
                    //chooseSubscriberPageTextList: getTextWithIconList(esimContentData.chooseSubscriberPageTextList.value),
                    instructionsPageTextList: getTextWithIconList(esimContentData.instructionsPageTextList.value),
                    successPageEsimNewLineTextList: getTextWithIconList(esimContentData.successPageEsimNewLineTextList.value),
                    videoPageTextList: getTextWithIconList(esimContentData.videoPageTextList.value),
                    scanQrFromMobileUrl: esimContentData.scanQrFromMobileUrl.value,
                    esimActivateVideoAndroidUrl: esimContentData.esimActivateVideoAndroidUrl.value,
                    esimActivateVideoIosUrl: esimContentData.esimActivateVideoIosUrl.value,
                    isDisplayReplaceSimCheckbox: !!esimContentData.isDisplayReplaceSimCheckbox.value,
                    instructionsPageUrl: esimContentData.instructionsPageUrl.value,
                    instructionsTextAndroid: esimContentData.instructionsTextAndroid.value,
                    instructionsTextIos: esimContentData.instructionsTextIos.value,
                    termsOfServiceUrl: getUrlFromEpiLink(esimContentData.termsOfServiceUrl),
                };

                //let page = 'otpPage';
                //page = 'validateOtpPage';
                //page = 'replaceSimToEsimPage';
                //page = 'successPage';
                //page = 'chooseSubscriberPage';
                //page = 'instructionsPage';
                //page = 'activationCodesPage';
                //page = 'videoPage';
                //page = 'errorPage';

                let payload = {
                    esimBlockData: esimBlockData,
                };
                return { payload: payload };
            }
        },
        goToPage: (state, action) => {
            state.page = action.payload;
            window.scrollTo(0, 0);
        },
        goToPageWithoutHistory: (state, action) => {
            state.page = action.payload;
            window.scrollTo(0, 0);
            if (typeof window !== 'undefined' && action.payload) {
                window.history.replaceState(
                    null,
                    '',
                    `${window.location.pathname}${window.location.search}#${action.payload}`,
                );
            }
        },
        resetAuthOtpTickets: (state) => {
            state.loginDetails.otpTicketId = '';
            state.loginDetails.securityOtpTicketId = '';
            state.loginDetails.securityOtpPhone = '';
            state.loginDetails.restrictedCodeRequired = false;
        },
        resetSecurityOtpTickets: (state) => {
            state.loginDetails.securityOtpTicketId = '';
            state.loginDetails.securityOtpPhone = '';
            state.loginDetails.restrictedCodeRequired = false;
        },
        setLoginDetails: (state, action) => {
            state.loginDetails.identity = action.payload.identity ? action.payload.identity : state.loginDetails.identity;
            state.loginDetails.subscriberNo = action.payload.subscriberNo ? action.payload.subscriberNo : state.loginDetails.subscriberNo;
            state.loginDetails.otpTicketId = action.payload.otpTicketId ? action.payload.otpTicketId : state.loginDetails.otpTicketId;
            state.loginDetails.securityOtpTicketId = action.payload.securityOtpTicketId ? action.payload.securityOtpTicketId : state.loginDetails.securityOtpTicketId;
            state.loginDetails.securityOtpPhone = action.payload.securityOtpPhone ? action.payload.securityOtpPhone : state.loginDetails.securityOtpPhone;
            if (action.payload.restrictedCodeRequired !== undefined) {
                state.loginDetails.restrictedCodeRequired = Boolean(action.payload.restrictedCodeRequired);
            }
        },
        setSubscriberList: (state, action) => {
            state.subscriberList = action.payload;
        },
        setEmail: (state, action) => {
            state.email = action.payload;
        },
        setIsLoadingQrCode: (state, action) => {
            state.isLoadingQrCode = action.payload;
        },
        setIsGetQrCodeError: (state, action) => {
            state.isGetQrCodeError = action.payload;
        },
        setOrderDetails: (state, action) => {
            state.orderDetails.orderId = action.payload.orderId;
        },
        setQrCodeDetails: (state, action) => {
            state.qrCodeDetails = action.payload;
        },
        setCheckUserResponse: (state, action) => {
            state.checkUserResponse.isJet = action.payload.isJet;
            state.checkUserResponse.primaryEmail = action.payload.primaryEmail;
            state.checkUserResponse.idNumber = action.payload.idNumber;
            state.checkUserResponse.firstName = action.payload.firstName;
            state.checkUserResponse.lastName = action.payload.lastName;
            state.checkUserResponse.isBusiness = action.payload.isBusiness;
        },
        setCellcomAppDetails: (state, action) => {
            state.isCellcomAppActive = action.payload.isCellcomAppActive;
            state.tinyAppUrl = action.payload.tinyAppUrl;
        },
        setErrorPage: (state, action) => {
            window.scrollTo(0, 0);
            state.errorDescription = action.payload.errorDescription;
            state.page = 'errorPage';
        },
        reportDWH: (state, action) => { },
    },
    extraReducers: {
        [prepareBlockAsync.fulfilled]: (state, action) => {
            if (!action.payload) { return; }
            //state.saleCatalog = action.payload.saleCatalog;
        },
        [getPriceEsimByUserParametersAsync.fulfilled]: (state, action) => {
            if (action.payload?.Header?.ReturnCode != 0) { return; }
            state.esimPrice = action.payload?.Body?.[getEsimTypeCode()];
        },
        [getSubscribersAsync.fulfilled]: (state, action) => {
            if (!action.payload) { return; }
            state.subscriberList = action.payload;
        },
    },
});

export const {
    resetBlock,
    initBlock,
    goToPage,
    goToPageWithoutHistory,
    resetAuthOtpTickets,
    resetSecurityOtpTickets,
    setLoginDetails,
    setSubscriberList,
    setEmail,
    setIsLoadingQrCode,
    setIsGetQrCodeError,
    setOrderDetails,
    setCellcomAppDetails,
    setQrCodeDetails,
    setCheckUserResponse,
    setErrorPage,
    reportDWH
} = esimUpgradeBlockSlice.actions;

export default esimUpgradeBlockSlice.reducer;


function getTextWithIconList(list) {
    if (!list) { return []; }
    const textWithIconList = list.map(item => {
        return {
            icon: item.icon,
            title: item.title,
            text: item.text,
            isBoldText: !!item.isBoldText
        }
    });
    return textWithIconList;
}
